import { DateRangeSelector } from '@app/components/date-range-selector/DateRangeSelector';
import {
  DateRangePreset,
  DateRangeValue,
} from '@app/components/date-range-selector/date-range-presets';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Avatar, Box, Button, Checkbox, IconButton, Typography } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

export type YoutubeStatsLegendItem = {
  channelId: string;
  label: string;
  thumbnailUrl?: string;
  color: string;
  checked: boolean;
};

export type YoutubeStatsCustomLegendProps = {
  items: YoutubeStatsLegendItem[];
  ownChannelId: string | null;
  dateRange: DateRangeValue;
  dateRangePreset: DateRangePreset;
  onDateRangeChange: (range: DateRangeValue, preset: DateRangePreset) => void;
  onDateRangeReset: () => void;
  isDateRangeResetDisabled: boolean;
  onToggleChannel: (channelId: string) => void;
  onSelectAllOthers: () => void;
  onSelectNoneOthers: () => void;
};

const YOUTUBE_STATS_ALLOWED_PRESETS: DateRangePreset[] = [
  'lastHour',
  { type: 'lastNHours', hours: 3 },
  { type: 'lastNHours', hours: 6 },
  { type: 'lastNHours', hours: 12 },
  'last24Hours',
  'today',
  'yesterday',
  'customSingleDate',
];

const channelsHeaderMessage = defineMessage({
  id: 'logger.youtubeStats.legend.channels',
  defaultMessage: 'Canales',
});

const ownChannelHeaderMessage = defineMessage({
  id: 'logger.youtubeStats.legend.ownChannel',
  defaultMessage: 'Canal de la emisora',
});

const otherChannelsHeaderMessage = defineMessage({
  id: 'logger.youtubeStats.legend.otherChannels',
  defaultMessage: 'Otros canales',
});

const selectAllMessage = defineMessage({
  id: 'logger.youtubeStats.legend.selectAll',
  defaultMessage: 'Seleccionar todos',
});

const selectNoneMessage = defineMessage({
  id: 'logger.youtubeStats.legend.selectNone',
  defaultMessage: 'Seleccionar ninguno',
});

const DATE_SELECTOR_BUTTON_PROPS = {
  size: 'small' as const,
  variant: 'outlined' as const,
  color: 'secondary' as const,
  sx: {
    minHeight: 40,
    backgroundColor: 'background.paper',
  },
};

const LEGEND_CONTAINER_SX = {
  width: 320,
  height: 640,
  display: 'flex',
  flexDirection: 'column' as const,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  p: 2,
};

/**
 * Left-side legend for YouTube stats with persisted checkbox toggles and grouped channel sections.
 */
export function YoutubeStatsCustomLegend(props: YoutubeStatsCustomLegendProps) {
  const {
    items,
    ownChannelId,
    dateRange,
    dateRangePreset,
    onDateRangeChange,
    onDateRangeReset,
    isDateRangeResetDisabled,
    onToggleChannel,
    onSelectAllOthers,
    onSelectNoneOthers,
  } = props;
  const { formatMessage: $t } = useIntl();

  const ownChannelItem = ownChannelId
    ? items.find((item) => item.channelId === ownChannelId)
    : null;
  const otherItems = items.filter((item) => item.channelId !== ownChannelId);
  const hasGroups = Boolean(ownChannelItem);

  const renderDateRangeSelector = () => {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 1, gap: 1 }}
      >
        <DateRangeSelector
          value={dateRange}
          preset={dateRangePreset}
          onChange={onDateRangeChange}
          allowedPresets={YOUTUBE_STATS_ALLOWED_PRESETS}
          buttonProps={DATE_SELECTOR_BUTTON_PROPS}
        />
        <IconButton
          color="secondary"
          onClick={onDateRangeReset}
          disabled={isDateRangeResetDisabled}
        >
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const renderItem = (item: YoutubeStatsLegendItem) => {
    return (
      <Button
        key={item.channelId}
        onClick={() => onToggleChannel(item.channelId)}
        sx={{
          p: 0,
          width: '100%',
          justifyContent: 'flex-start',
          textTransform: 'none',
          color: 'text.primary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Checkbox
          checked={item.checked}
          disableRipple
          sx={{
            color: item.color,
            '&.Mui-checked': {
              color: item.color,
            },
          }}
        />
        {item.thumbnailUrl ? (
          <Avatar
            src={item.thumbnailUrl}
            variant="circular"
            sx={{
              width: 20,
              height: 20,
              flexShrink: 0,
              mr: 1,
              border: '1px solid',
              borderColor: 'grey.400',
            }}
          />
        ) : null}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {item.label}
        </Typography>
      </Button>
    );
  };

  const renderOtherChannelsActions = () => {
    if (!hasGroups) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="text"
          onClick={onSelectAllOthers}
          sx={{ p: 0, minWidth: 0, typography: 'caption', textTransform: 'none' }}
        >
          {$t(selectAllMessage)}
        </Button>
        <Typography variant="caption" color="text.secondary">
          /
        </Typography>
        <Button
          variant="text"
          onClick={onSelectNoneOthers}
          sx={{ p: 0, minWidth: 0, typography: 'caption', textTransform: 'none' }}
        >
          {$t(selectNoneMessage)}
        </Button>
      </Box>
    );
  };

  const channelsSectionTitle = hasGroups ? otherChannelsHeaderMessage : channelsHeaderMessage;
  const channelsSectionItems = hasGroups ? otherItems : items;

  const renderChannelsList = () => {
    return (
      <Box sx={{ minHeight: 0, overflowY: 'auto', flex: hasGroups ? 1 : undefined }}>
        {channelsSectionItems.map(renderItem)}
      </Box>
    );
  };

  if (!hasGroups) {
    return (
      <Box sx={{ ...LEGEND_CONTAINER_SX, gap: 1 }}>
        <Typography variant="subtitle2">{`${$t(channelsHeaderMessage)}`}</Typography>
        {renderDateRangeSelector()}
        {renderChannelsList()}
      </Box>
    );
  }

  return (
    <Box sx={{ ...LEGEND_CONTAINER_SX, gap: 2 }}>
      {renderDateRangeSelector()}

      {ownChannelItem ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2">{`${$t(ownChannelHeaderMessage)}`}</Typography>
          {renderItem(ownChannelItem)}
        </Box>
      ) : null}

      <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="subtitle2">{`${$t(channelsSectionTitle)}`}</Typography>
          {renderOtherChannelsActions()}
        </Box>
        {renderChannelsList()}
      </Box>
    </Box>
  );
}
